import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  theme,
  Typography,
} from "antd";
import { InfoCircleFilled, PlusOutlined } from "@ant-design/icons";
import {
  CARD_SPLIT_OPTIONS,
  IN_PERSON_BUCKET_META,
  InPersonBucketKey,
  InPersonBucketState,
  InPersonPricingState,
  orderedBucketKeys,
  validateInPersonState,
} from "./partnerPricing";
import PricingBucketsTable, { PricingRow } from "./PricingBucketsTable";
import {
  IPurchPriceProfile,
  PricingTemplate,
} from "../../../../../interfaces/partner.interface";
import { WizardMode } from "../wizardSteps";

const { Text, Title } = Typography;

interface Props {
  value: InPersonPricingState;
  onChange: (next: InPersonPricingState) => void;
  mode?: WizardMode;
  initial?: InPersonPricingState;
  profiles?: IPurchPriceProfile[];
  onProfileCreate?: (
    name: string,
    whitelist: string[],
    draft: InPersonPricingState
  ) => Promise<boolean>;
  onProfileSelect?: (profileId: string) => void;
  creatingProfile?: boolean;
  onNewRangeVisibleChange?: (visible: boolean) => void;
  showVendor?: boolean;
  saveError?: { message?: string; code?: string };
}

const cloneInPersonPricingState = (
  state: InPersonPricingState
): InPersonPricingState => ({
  ...state,
  buckets: Object.fromEntries(
    Object.entries(state.buckets).map(([key, bucket]) => [key, { ...bucket }])
  ) as InPersonPricingState["buckets"],
  profileWhitelist: state.profileWhitelist
    ? [...state.profileWhitelist]
    : undefined,
  extraProfiles: state.extraProfiles ? [...state.extraProfiles] : undefined,
});

export default function PartnerInPersonPricingStep({
  value,
  onChange,
  mode = "create",
  initial,
  profiles,
  onProfileSelect,
  onProfileCreate,
  creatingProfile,
  onNewRangeVisibleChange,
  showVendor = true,
  saveError,
}: Props) {
  const { token } = theme.useToken();
  const [newRangeVisible, setNewRangeVisible] = useState(false);
  const [rangeName, setRangeName] = useState("");
  const [rangeWhitelist, setRangeWhitelist] = useState("");
  const [rangeDraft, setRangeDraft] = useState<InPersonPricingState>();
  const editingValue = rangeDraft ?? value;

  useEffect(() => {
    onNewRangeVisibleChange?.(newRangeVisible);
    return () => onNewRangeVisibleChange?.(false);
  }, [newRangeVisible, onNewRangeVisibleChange]);

  const keys = orderedBucketKeys(
    editingValue.template,
    editingValue.includeAmex
  );
  const errorByKey = useMemo(() => {
    const map: Partial<Record<InPersonBucketKey, string>> = {};
    validateInPersonState(editingValue).forEach((e) => {
      if (!map[e.key]) map[e.key] = e.message;
    });
    return map;
  }, [editingValue]);

  const dirtyKeys = useMemo(() => {
    const set = new Set<string>();
    // Saved range, so an unsaved invalid bucket copied into the draft stays highlighted.
    const baseline = initial;
    if (mode !== "edit" || !baseline) return set;
    keys.forEach((key) => {
      if (
        JSON.stringify(editingValue.buckets[key]) !==
        JSON.stringify(baseline.buckets[key])
      ) {
        set.add(key);
      }
    });
    return set;
  }, [mode, initial, editingValue, keys]);

  const cardSplitMeta = CARD_SPLIT_OPTIONS.find(
    (o) => o.value === editingValue.template
  )!;

  const updateBucket = (key: string, patch: Partial<InPersonBucketState>) => {
    const next = {
      ...editingValue,
      buckets: {
        ...editingValue.buckets,
        [key]: {
          ...editingValue.buckets[key as InPersonBucketKey],
          ...patch,
        },
      },
    };
    if (rangeDraft) {
      setRangeDraft(next);
      return;
    }
    onChange(next);
  };

  const applyCardSplit = (template: PricingTemplate) =>
    onChange({ ...value, template });

  // A draft range being created holds its own split until it is saved, so it
  // must not write through to the selected one.
  const changeCardSplit = (template: PricingTemplate) => {
    if (template === editingValue.template) return;
    if (rangeDraft) {
      setRangeDraft({ ...rangeDraft, template });
      return;
    }
    if (mode === "edit") {
      Modal.confirm({
        title: "Change card split?",
        content:
          "Switching splits changes which card buckets apply. Any bucket that isn't in the new split is dropped when you save.",
        okText: "Change split",
        cancelText: "Keep current",
        onOk: () => applyCardSplit(template),
      });
      return;
    }
    applyCardSplit(template);
  };

  // In edit, only surface validation on buckets the user actually changed, so a
  // legacy partner with out-of-range stored data doesn't open all-red.
  const rows: PricingRow[] = keys.map((key) => ({
    key,
    label: IN_PERSON_BUCKET_META[key].label,
    state: editingValue.buckets[key],
    error: mode !== "edit" || dirtyKeys.has(key) ? errorByKey[key] : undefined,
  }));

  const profileOptions = useMemo(() => {
    if (!profiles || profiles.length === 0)
      return [{ value: "default", label: "Default" }];
    return profiles.map((p) => ({ value: p._id, label: p.name || "Default" }));
  }, [profiles]);

  const activeProfileId = value.profileId ?? profiles?.[0]?._id ?? "default";
  const submitNewRange = async () => {
    const name = rangeName.trim();
    if (!name || !rangeDraft) return;
    const whitelist = rangeWhitelist
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const created = await onProfileCreate?.(name, whitelist, rangeDraft);
    if (!created) return;
    setRangeName("");
    setRangeWhitelist("");
    setRangeDraft(undefined);
    setNewRangeVisible(false);
  };

  const cancelNewRange = () => {
    setRangeName("");
    setRangeWhitelist("");
    setRangeDraft(undefined);
    setNewRangeVisible(false);
  };

  const saveErrorAlert = saveError ? (
    <Alert
      type="error"
      showIcon
      style={{ marginTop: 12 }}
      message="In-person pricing was not saved"
      description={
        [saveError.code, saveError.message].filter(Boolean).join(": ") ||
        "The server rejected this in-person pricing. Nothing was saved."
      }
    />
  ) : null;

  const cardSplitContent = (
    <>
      <Title level={5} style={{ margin: 0 }}>
        Card split
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Which card buckets to use. Switching splits changes which buckets apply.
      </Text>
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "wrap",
        }}
      >
        <Select
          value={editingValue.template}
          onChange={changeCardSplit}
          options={CARD_SPLIT_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          style={{ width: 280 }}
          aria-label="Card split"
        />
        <div
          style={{
            flex: 1,
            minWidth: 280,
            display: "flex",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 12,
            background: token.colorPrimaryBg,
            border: `1px solid ${token.colorPrimaryBorder}`,
          }}
        >
          <InfoCircleFilled
            style={{ color: token.colorPrimary, fontSize: 16, marginTop: 2 }}
          />
          <div>
            <Text style={{ fontSize: 13 }}>
              <Text strong style={{ color: token.colorPrimary }}>
                {cardSplitMeta.label}
              </Text>{" "}
              - {cardSplitMeta.description}
            </Text>
            <div
              style={{
                display: "flex",
                gap: 6,
                marginTop: 8,
                flexWrap: "wrap",
              }}
            >
              {rows.map((row) => (
                <span
                  key={row.key}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: token.colorPrimary,
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorPrimaryBorder}`,
                    borderRadius: 6,
                    padding: "2px 8px",
                  }}
                >
                  {row.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      {saveErrorAlert}
    </>
  );

  const creatingRangeView = mode === "edit" && newRangeVisible;

  const cardBucketsContent = (
    <PricingBucketsTable
      title="Card buckets"
      subtitle="Klearly floor + partner markup = what the merchant pays. The bar shows the split live."
      rows={rows}
      onChange={updateBucket}
      dirtyKeys={dirtyKeys}
      showVendor={showVendor}
      bordered={!creatingRangeView}
    />
  );

  return (
    <div style={{ margin: "-4px -24px -16px" }}>
      {mode === "edit" && (
        <div style={{ padding: "20px 24px 16px" }}>
          {newRangeVisible ? (
            <div
              style={{
                border: `1px solid ${token.colorPrimaryBorder}`,
                borderRadius: 12,
                boxShadow: token.boxShadowTertiary,
                overflow: "hidden",
              }}
            >
              <div style={{ padding: 16 }}>
                <Title level={5} style={{ margin: 0 }}>
                  New partner price range
                </Title>
                <Text
                  type="secondary"
                  style={{ display: "block", marginBottom: 12 }}
                >
                  Give the range a name and an optional whitelist. Pricing
                  carries over from the range shown below.
                </Text>
                <Form layout="vertical" style={{ margin: 0 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "end" }}>
                    <Form.Item
                      label="Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        value={rangeName}
                        onChange={(e) => setRangeName(e.target.value)}
                        placeholder="e.g. Hospitality"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Whitelist (optional)"
                      style={{ flex: 1.4, marginBottom: 0 }}
                    >
                      <Input
                        value={rangeWhitelist}
                        onChange={(e) => setRangeWhitelist(e.target.value)}
                        placeholder="Comma-separated merchant KVKs or IDs"
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      onClick={submitNewRange}
                      disabled={!rangeName.trim()}
                      loading={creatingProfile}
                      style={{ width: 84, height: 40 }}
                    >
                      Create
                    </Button>
                    <Button
                      onClick={cancelNewRange}
                      disabled={creatingProfile}
                      style={{ width: 84, height: 40 }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              </div>
              <div style={{ padding: 16 }}>{cardSplitContent}</div>
              {cardBucketsContent}
            </div>
          ) : (
            <>
              <Title level={5} style={{ margin: 0 }}>
                Partner price range
              </Title>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 12 }}
              >
                The values below configure the selected range. Switch to view
                another, or create a new one.
              </Text>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Select
                  value={activeProfileId}
                  options={profileOptions}
                  onChange={onProfileSelect}
                  style={{ width: 280 }}
                  disabled={!onProfileSelect || profileOptions.length <= 1}
                />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setRangeDraft(cloneInPersonPricingState(value));
                    setNewRangeVisible(true);
                  }}
                >
                  New
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {!creatingRangeView && (
        <div style={{ padding: "20px 24px 16px" }}>{cardSplitContent}</div>
      )}

      {!creatingRangeView && cardBucketsContent}
    </div>
  );
}
